import React, { useMemo } from 'react';
import {
  ExternalLink,
  Package,
  Search,
  ChevronDown,
  ChevronRight,
  GitCommit,
  Users,
  GitPullRequest,
  CircleDot,
  ShieldAlert,
  AlertTriangle,
} from 'lucide-react';
import CommitHeatmap from '../../../components/CommitHeatmap/CommitHeatmap';
import CycleTimeSection from '../../../components/dora/CycleTimeSection';
import { CycleTimeSummaryMetricCards } from '../../../components/dora/CycleTimeSummaryMetricCard';
import CloneUrlBar from '../../../components/repository/CloneUrlBar';
import { getRepoDeploymentBranches } from '../../../utils/deploymentBranches';
import { commitHashClass } from '../../../components/repository/repoViewTheme';
import { card } from '../../../components/ui/uiTheme';
import { KeyMetricCard } from '../../../components/ui/KeyMetricCard';
import { FeatureGate } from '../../../contexts/FeatureFlagContext';
import { SectionSubtitle } from '../../../components/ui/PageLayout';

/**
 * Repository detail Overview tab: key metrics, about, activity heatmap, compact DORA strip, tech stack.
 */
export default function RepositoryOverviewTab({
  repositoryId,
  organisationId,
  repoData,
  deploymentDoraMeta,
  leadTimeDoraMeta,
  churnData,
  onGoToMetrics,
  metricsFetchScope,
  cycleTimeConfig,
  techStack,
  techStackLoading,
  techStackScanning,
  techCategoriesOpen,
  setTechCategoriesOpen,
  onScanTechStack,
}: any) {
  const totalCommits = repoData.stats?.totalCommits ?? 0;
  const contributors = repoData.stats?.contributors ?? 0;
  const openPrs = repoData.stats?.openPRs ?? 0;
  const openIssues = repoData.stats?.openIssues ?? 0;
  const sensitiveCommitCount = repoData.stats?.commitsWithSensitiveFindings ?? 0;
  const lastSensitiveAt = repoData.stats?.lastSensitiveDetectionAt;

  const deploymentFreqPerDay = useMemo(() => {
    const counts = deploymentDoraMeta?.mergeDeploymentsByDay?.counts;
    if (!Array.isArray(counts) || counts.length === 0) return null;
    const mean = counts.reduce((a, b) => a + b, 0) / counts.length;
    return mean.toFixed(1);
  }, [deploymentDoraMeta]);

  const leadTimeHours = useMemo(() => {
    const minutes = leadTimeDoraMeta?.meanLeadTimeMinutes;
    if (minutes == null || !Number.isFinite(minutes)) return null;
    return (minutes / 60).toFixed(1);
  }, [leadTimeDoraMeta]);

  const churnRatePct = useMemo(() => {
    const ratios = churnData?.ratios;
    if (!Array.isArray(ratios) || ratios.length === 0) return null;
    const mean = ratios.reduce((a, b) => a + b, 0) / ratios.length;
    return (mean * 100).toFixed(1);
  }, [churnData]);

  return (
    <div
      role="tabpanel"
      id="repo-tabpanel-overview"
      aria-labelledby="repo-tab-overview"
      className="dashboard-org-tight"
    >
      <FeatureGate flag="ui.repositoryDetail.overviewMetadata">
        <div className="section-block-mdl">
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-4">
            {repoData.lastSyncedAt && (
              <span>
                Last synced:{' '}
                {new Date(repoData.lastSyncedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            )}
            {(repoData.stats?.lastCommit || repoData.lastCommit) && (
              <span>
                Last commit:{' '}
                {new Date(repoData.stats?.lastCommit || repoData.lastCommit).toLocaleString(
                  'en-US',
                  {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  }
                )}
              </span>
            )}
            {sensitiveCommitCount > 0 ? (
              <span className="inline-flex items-center gap-1.5 rounded-md border-amber-700/50 bg-amber-950/35 px-2 py-1 text-amber-100 font-medium">
                <ShieldAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Sensitive scan: {sensitiveCommitCount} commit{sensitiveCommitCount === 1 ? '' : 's'}
                {lastSensitiveAt ? (
                  <>
                    {' '}
                    · Latest{' '}
                    {new Date(lastSensitiveAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </>
                ) : null}
              </span>
            ) : null}
            {(repoData.consecutiveSyncFailures ?? 0) >= 3 ? (
              <span
                className="inline-flex items-center gap-1.5 rounded-md border-amber-700/50 bg-amber-950/35 px-2 py-1 text-amber-100 font-medium"
                title={repoData.lastSyncError || undefined}
              >
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Sync failing: {repoData.consecutiveSyncFailures} attempt
                {repoData.consecutiveSyncFailures === 1 ? '' : 's'} in a row
                {repoData.lastSyncAttemptAt ? (
                  <>
                    {' '}
                    · Last attempt{' '}
                    {new Date(repoData.lastSyncAttemptAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </>
                ) : null}
              </span>
            ) : null}
          </div>

          <div className="card-grid-4-mdl">
            <KeyMetricCard
              label="Total commits"
              value={
                typeof totalCommits === 'number' ? totalCommits.toLocaleString() : totalCommits
              }
              icon={<GitCommit className="w-12 h-12 text-vivid-green-cyan opacity-20" />}
              footer={
                <p className="text-xs text-slate-500">
                  This week: {repoData.stats?.commitsThisWeek ?? 0}
                  <span className="mx-1">·</span>
                  This month: {repoData.stats?.commitsThisMonth ?? 0}
                </p>
              }
            />
            <KeyMetricCard
              label="Contributors"
              value={contributors}
              icon={<Users className="w-12 h-12 text-vivid-purple opacity-20" />}
            />
            <KeyMetricCard
              label="Open pull requests"
              value={openPrs}
              icon={<GitPullRequest className="w-12 h-12 text-vivid-cyan-blue opacity-20" />}
            />
            <KeyMetricCard
              label="Open issues"
              value={openIssues}
              icon={<CircleDot className="w-12 h-12 text-luminous-vivid-amber opacity-20" />}
            />
          </div>
        </div>

        <div className="section-block-mdl">
          <div className={`${card.default} p-5`}>
            <h3 className="text-sm font-semibold text-slate-100 mb-3">About this repository</h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-400">
              <div>
                <dt className="text-slate-500">Language</dt>
                <dd className="font-medium text-slate-100">{repoData.language || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">License</dt>
                <dd className="font-medium text-slate-100">{repoData.license || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Deployment branches</dt>
                <dd className="font-medium text-slate-100">
                  {getRepoDeploymentBranches(repoData).map((branch, i) => (
                    <code key={branch} className={`${commitHashClass}${i > 0 ? ' ml-2' : ''}`}>
                      {branch}
                      {i === 0 ? ' (primary)' : ''}
                    </code>
                  ))}
                </dd>
              </div>
              {repoData.qaBranch && (
                <div>
                  <dt className="text-slate-500">QA branch</dt>
                  <dd className="font-medium text-slate-100">
                    <code className={commitHashClass}>{repoData.qaBranch}</code>
                  </dd>
                </div>
              )}
              {repoData.uatBranch && (
                <div>
                  <dt className="text-slate-500">UAT branch</dt>
                  <dd className="font-medium text-slate-100">
                    <code className={commitHashClass}>{repoData.uatBranch}</code>
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-slate-500">Visibility</dt>
                <dd className="font-medium text-slate-100">
                  {repoData.isPrivate ? 'Private' : 'Public'}
                </dd>
              </div>
              {(repoData.htmlUrl || repoData.url) && (
                <>
                  <div>
                    <dt className="text-slate-500">GitHub</dt>
                    <dd>
                      <a
                        href={repoData.htmlUrl || repoData.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-vivid-red hover:text-primary-600 font-medium"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> Open repository
                      </a>
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-slate-500 mb-1.5">Clone URL</dt>
                    <dd>
                      <CloneUrlBar htmlUrl={repoData.htmlUrl || repoData.url} />
                    </dd>
                  </div>
                </>
              )}
              {repoData.createdAt && (
                <div>
                  <dt className="text-slate-500">Created</dt>
                  <dd className="font-medium text-slate-100">
                    {new Date(repoData.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </FeatureGate>

      <FeatureGate flag="ui.repositoryDetail.cycleTimeSection">
        <div className="section-block-mdl">
          <CycleTimeSection
            className="!mt-0"
            fetchScope={metricsFetchScope}
            cycleTimeConfig={cycleTimeConfig}
          />
        </div>
        <div className="section-block-mdl">
          <CycleTimeSummaryMetricCards
            fetchScope={metricsFetchScope}
            cycleTimeConfig={cycleTimeConfig}
          />
        </div>
      </FeatureGate>

      <FeatureGate flag="ui.repositoryDetail.doraSection">
        <div className="section-block-mdl">
          <CommitHeatmap
            scope="repository"
            repositoryId={repositoryId}
            organisationId={organisationId}
            title="Activity Heatmap"
          />
        </div>

        <div className="section-block-mdl">
          <div className={`${card.default} px-4 py-3 flex flex-wrap items-center gap-x-2 gap-y-2`}>
            <span className="text-slate-500 text-sm">
              Deployment freq
              <span className="font-bold text-slate-100 text-sm ml-1">
                {deploymentFreqPerDay != null ? `${deploymentFreqPerDay}/day` : '—'}
              </span>
            </span>
            <span className="w-px bg-dark-border self-stretch mx-1" aria-hidden />
            <span className="text-slate-500 text-sm">
              Lead time
              <span className="font-bold text-slate-100 text-sm ml-1">
                {leadTimeHours != null ? `${leadTimeHours}h` : '—'}
              </span>
            </span>
            <span className="w-px bg-dark-border self-stretch mx-1" aria-hidden />
            <span className="text-slate-500 text-sm">
              Churn rate
              <span className="font-bold text-slate-100 text-sm ml-1">
                {churnRatePct != null ? `${churnRatePct}%` : '—'}
              </span>
            </span>
            {onGoToMetrics ? (
              <button
                type="button"
                onClick={onGoToMetrics}
                className="ml-auto text-xs text-vivid-red font-medium hover:underline shrink-0"
              >
                Full metrics →
              </button>
            ) : null}
          </div>
        </div>
      </FeatureGate>

      <FeatureGate flag="ui.repositoryDetail.techStackSection">
        <div className="section-block-mdl">
          <div className={`${card.default} p-4`}>
            <div className="flex items-center justify-between mb-3">
              <SectionSubtitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-vivid-purple" /> Technologies
              </SectionSubtitle>
              {techStack && (
                <span className="text-xs text-slate-500">
                  Scanned {new Date(techStack.scannedAt).toLocaleDateString()}
                </span>
              )}
            </div>
            {techStackLoading ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : !techStack || !techStack.items?.length ? (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-slate-400">
                  {techStack ? 'No technologies detected.' : 'Tech stack not scanned yet.'}
                </p>
                <button
                  type="button"
                  onClick={onScanTechStack}
                  disabled={techStackScanning}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-vivid-purple hover:bg-vivid-purple/90 disabled:opacity-50"
                >
                  <Search className={`w-4 h-4 mr-2 ${techStackScanning ? 'animate-pulse' : ''}`} />{' '}
                  {techStackScanning ? 'Scanning…' : 'Scan now'}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {['framework', 'runtime', 'library', 'tool', 'plugin', 'language'].map((cat) => {
                  const items = techStack.items.filter((i) => i.category === cat);
                  if (items.length === 0) return null;
                  const isOpen = techCategoriesOpen.has(cat);
                  return (
                    <div key={cat}>
                      <button
                        type="button"
                        onClick={() =>
                          setTechCategoriesOpen((prev) => {
                            const next = new Set(prev);
                            if (next.has(cat)) next.delete(cat);
                            else next.add(cat);
                            return next;
                          })
                        }
                        className="w-full flex items-center gap-2 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500 hover:bg-dark-elevated/50 rounded"
                      >
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                        <span className="capitalize">{cat}</span> ({items.length})
                      </button>
                      {isOpen && (
                        <div className="pb-2 flex flex-wrap gap-2 pl-6">
                          {items.map((item) => (
                            <span
                              key={item.id}
                              className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border border-dark-border bg-dark-elevated text-slate-200"
                              title={`${item.source}${item.version ? ` • ${item.version}` : ''}`}
                            >
                              {item.name}
                              {item.version && (
                                <span className="ml-1 text-slate-500">({item.version})</span>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={onScanTechStack}
                  disabled={techStackScanning}
                  className="text-sm text-vivid-purple hover:text-vivid-purple/80 disabled:opacity-50 mt-2"
                >
                  Re-scan tech stack
                </button>
              </div>
            )}
          </div>
        </div>
      </FeatureGate>
    </div>
  );
}
